import React from 'react';
import { Button, Flex, Text, TextField, TextArea } from '@chatsdet/ui';
import { ClipboardText, LinkSimple, Paperclip } from '@phosphor-icons/react';
import {
  useAddContextLink,
  useAddContextPaste,
  useUploadContextDocument,
  type ContextDocument,
} from '../useContextLibrary';

type ContextUploadZoneProps = {
  /** Called with the newly created documents after any successful add. */
  onAdded: (docs: ContextDocument[]) => void;
  /** Show the "Paste text" affordance (the library page wants it; the compact
   *  exploratory modal does not). */
  allowPaste?: boolean;
};

const FILE_ACCEPT =
  'image/*,application/pdf,text/plain,.txt,text/markdown,.md,text/csv,.csv,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx';

/**
 * Reusable "add to context library" surface: a drag/drop file zone, an add-link
 * row, and (optionally) a paste-text panel. Shared by the standalone Context
 * Library page and the exploratory-test creation modal so the upload UX lives
 * in one place.
 */
export function ContextUploadZone({ onAdded, allowPaste = false }: ContextUploadZoneProps) {
  const uploadMutation = useUploadContextDocument();
  const linkMutation = useAddContextLink();
  const pasteMutation = useAddContextPaste();
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const [linkUrl, setLinkUrl] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [isDragActive, setIsDragActive] = React.useState(false);
  const [pasteOpen, setPasteOpen] = React.useState(false);
  const [pasteLabel, setPasteLabel] = React.useState('');
  const [pasteContent, setPasteContent] = React.useState('');
  // Depth counter so dragenter/dragleave bubbling across the zone's children
  // doesn't flicker the active state — only a leave returning depth to zero
  // (the pointer actually exiting the zone) clears it.
  const dragDepth = React.useRef(0);

  const uploadFiles = React.useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      setError(null);
      uploadMutation.mutate(files, {
        onSuccess: onAdded,
        onError: (err) => setError(err instanceof Error ? err.message : 'Failed to upload file'),
      });
    },
    [onAdded, uploadMutation]
  );

  const isFileDrag = (event: React.DragEvent<HTMLDivElement>) =>
    Array.from(event.dataTransfer.types ?? []).includes('Files');

  const handleDragEnter = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    dragDepth.current += 1;
    setIsDragActive(true);
  }, []);

  const handleDragOver = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDragLeave = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragActive(false);
  }, []);

  const handleDrop = React.useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      dragDepth.current = 0;
      setIsDragActive(false);
      uploadFiles(Array.from(event.dataTransfer.files ?? []));
    },
    [uploadFiles]
  );

  const handlePick = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      uploadFiles(Array.from(event.target.files ?? []));
      if (inputRef.current) inputRef.current.value = '';
    },
    [uploadFiles]
  );

  const handleAddLink = React.useCallback(() => {
    const url = linkUrl.trim();
    if (!url) return;
    setError(null);
    linkMutation.mutate(
      { url },
      {
        onSuccess: (doc) => {
          onAdded([doc]);
          setLinkUrl('');
        },
        onError: (err) => setError(err instanceof Error ? err.message : 'Failed to add link'),
      }
    );
  }, [linkMutation, linkUrl, onAdded]);

  const handleAddPaste = React.useCallback(() => {
    const content = pasteContent.trim();
    if (!content) return;
    setError(null);
    pasteMutation.mutate(
      { content, label: pasteLabel.trim() || undefined },
      {
        onSuccess: (doc) => {
          onAdded([doc]);
          setPasteContent('');
          setPasteLabel('');
          setPasteOpen(false);
        },
        onError: (err) => setError(err instanceof Error ? err.message : 'Failed to save text'),
      }
    );
  }, [onAdded, pasteContent, pasteLabel, pasteMutation]);

  return (
    <div className="space-y-3">
      <div
        data-dropzone
        data-drag-active={isDragActive || undefined}
        className={[
          'rounded-lg border border-dashed p-5 text-center transition-colors',
          isDragActive
            ? 'border-primary bg-primary/10 ring-1 ring-primary/40'
            : 'border-border bg-muted/30',
        ].join(' ')}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Flex direction="column" align="center" gap="sm">
          <Paperclip
            size={20}
            className={isDragActive ? 'text-primary' : 'text-muted-foreground'}
          />
          <Text size="sm" className={isDragActive ? 'text-primary' : 'text-muted-foreground'}>
            {isDragActive
              ? 'Drop to add to your library'
              : 'Drop files here — PDFs, docs, images, CSV, text.'}
          </Text>
          <Flex align="center" gap="sm">
            <Button
              variant="secondary"
              size="sm"
              onPress={() => inputRef.current?.click()}
              isPending={uploadMutation.isPending}
            >
              Choose files
            </Button>
            {allowPaste && (
              <Button
                variant="ghost"
                size="sm"
                onPress={() => setPasteOpen((open) => !open)}
              >
                <Flex align="center" gap="sm">
                  <ClipboardText size={16} />
                  Paste text
                </Flex>
              </Button>
            )}
          </Flex>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={FILE_ACCEPT}
            className="hidden"
            onChange={handlePick}
          />
        </Flex>
      </div>

      {pasteOpen && (
        <div className="space-y-2 rounded-lg border border-border bg-background p-3">
          <TextField label="Title (optional)" value={pasteLabel} onChange={setPasteLabel} />
          <TextArea
            label="Text"
            placeholder="Paste notes, requirements, runbook steps…"
            value={pasteContent}
            onChange={setPasteContent}
            rows={5}
          />
          <Flex justify="end" gap="sm">
            <Button variant="ghost" size="sm" onPress={() => setPasteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onPress={handleAddPaste}
              isPending={pasteMutation.isPending}
              isDisabled={!pasteContent.trim()}
            >
              Save text
            </Button>
          </Flex>
        </div>
      )}

      <Flex align="end" gap="sm">
        <div className="flex-1">
          <TextField
            label="Add a link"
            placeholder="https://…"
            value={linkUrl}
            onChange={setLinkUrl}
          />
        </div>
        <Button
          variant="secondary"
          onPress={handleAddLink}
          isPending={linkMutation.isPending}
          isDisabled={!linkUrl.trim()}
        >
          <Flex align="center" gap="sm">
            <LinkSimple size={16} />
            Add
          </Flex>
        </Button>
      </Flex>
      <Text size="xs" className="text-muted-foreground">
        Links must be publicly accessible (e.g. a published doc) — pages behind a login won&apos;t
        be read.
      </Text>

      {error && (
        <Text size="xs" className="text-destructive">
          {error}
        </Text>
      )}
    </div>
  );
}
